import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import BillingAddressModal from '../components/BillingAddressModal';
import { useAuth } from '../contexts/AuthContext';
import { clearPendingSelection, formatMoney, markPaygChosen } from '../utils/choosePlan';
import { hasBillingAddress, runSubscriptionCheckout } from '../utils/subscriptionCheckout';
import AutopayConsent from '../components/AutopayConsent';
import './PricingBilling.css';
import './CheckoutPage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface CatalogPlan {
  id: string;
  name: string;
  priceMonthly: number;
  currency?: 'INR' | 'USD';
  price?: number;
  limits: Array<{ featureKey: string; label: string; maxPerMonth: number | null }>;
}

interface CatalogPaygItem {
  featureKey: string;
  label: string;
  unit: string;
  pricePerUnit: number;
  currency?: 'INR' | 'USD';
  price?: number;
}

interface CatalogResponse {
  plans: CatalogPlan[];
  payg: CatalogPaygItem[];
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const planId = params.get('plan');
  const isPayg = params.get('mode') === 'payg';

  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/pricing/catalog`, { credentials: 'include' });
        if (!res.ok) throw new Error('catalog');
        const data = (await res.json()) as CatalogResponse;
        if (!cancelled) setCatalog(data);
      } catch {
        if (!cancelled) setLoadError('Unable to load checkout details right now.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const plan = catalog && planId ? catalog.plans.find((p) => p.id === planId) ?? null : null;
  const invalid = Boolean(catalog) && !isPayg && !plan;

  const finish = (message: string) => {
    clearPendingSelection();
    setDone(message);
    setTimeout(() => navigate('/dashboard', { replace: true }), 1800);
  };

  const [autopay, setAutopay] = useState(false);

  const payForPlan = async (addressChecked = false) => {
    if (!plan) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      // A paid plan needs an invoice address first; the modal re-runs this once it is saved.
      if (plan.priceMonthly > 0 && !addressChecked && !(await hasBillingAddress())) {
        setAddressModalOpen(true);
        return;
      }
      const outcome = await runSubscriptionCheckout({ planId: plan.id, planName: plan.name, oneTime: plan.priceMonthly > 0 && !autopay, onStatus: setStatus });
      if (outcome === 'external') {
        setStatus('Checkout opened in a new tab. Complete payment there, then return to the dashboard.');
        return;
      }
      if (outcome === 'pending') {
        finish('Payment received. Your plan will activate in a minute.');
        return;
      }
      finish(outcome === 'free' ? `${plan.name} activated.` : `${plan.name} is active. You can start using the features.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to complete checkout');
    } finally {
      setBusy(false);
    }
  };

  const activatePayg = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/me/pricing/mode`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'payg' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Switch failed' })) as { error?: string };
        throw new Error(data.error || 'Switch failed');
      }
      if (user?.id) markPaygChosen(user.id);
      finish('Pay as you go is on. You are charged only for what you use.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Switch failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pricing-shell billing-page-shell co-shell">
      <div className="pricing-hero billing-hero">
        <div className="billing-hero-actions">
          <Link to="/choose-plan" className="billing-btn secondary billing-back-btn">Back</Link>
          <span className="billing-hero-kicker">Checkout</span>
        </div>
        <h1 className="pricing-title">Complete your order</h1>
        <p className="pricing-subtitle">Review your selection, then pay to start using the features.</p>
      </div>

      {loadError && <div className="pricing-error">{loadError}</div>}
      {!catalog && !loadError && <div className="pricing-loading">Loading checkout…</div>}

      {invalid && (
        <div className="co-card">
          <p className="co-note">That plan is not available any more.</p>
          <Link to="/choose-plan" className="billing-btn">Choose a plan</Link>
        </div>
      )}

      {catalog && plan && (
        <div className="co-card">
          <div className="co-row">
            <div className="co-row-main">
              <h2 className="co-name">{plan.name}</h2>
              <p className="co-note">{plan.priceMonthly > 0 && !autopay ? 'One month, no automatic renewal.' : 'Monthly subscription, renews every month.'}</p>
            </div>
            <div className="co-amount">
              {formatMoney(plan.currency, plan.price ?? plan.priceMonthly)}
              <span className="co-per"> / month</span>
            </div>
          </div>
          <ul className="co-list">
            {plan.limits.slice(0, 6).map((limit) => (
              <li key={limit.featureKey}>{limit.label}: {limit.maxPerMonth === null ? 'Unlimited' : limit.maxPerMonth}</li>
            ))}
          </ul>
          {plan.priceMonthly > 0 && (
            <AutopayConsent
              checked={autopay}
              onChange={setAutopay}
              amountLabel={formatMoney(plan.currency, plan.price ?? plan.priceMonthly)}
              disabled={busy}
            />
          )}
          <div className="co-total">
            <span>Due today</span>
            <strong>{formatMoney(plan.currency, plan.price ?? plan.priceMonthly)}</strong>
          </div>
        </div>
      )}

      {catalog && isPayg && (
        <div className="co-card">
          <div className="co-row">
            <div className="co-row-main">
              <h2 className="co-name">Pay as you go</h2>
              <p className="co-note">No monthly fee and nothing to pay today. Each use is charged at the rates below.</p>
            </div>
            <div className="co-amount">{formatMoney(undefined, 0)}</div>
          </div>
          <ul className="co-list co-list-rates">
            {catalog.payg.map((item) => (
              <li key={item.featureKey}>
                <span>{item.label}</span>
                <span>{formatMoney(item.currency, item.price ?? item.pricePerUnit)} / {item.unit}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {status && !done && <div className="co-status">{status}</div>}
      {error && <div className="pricing-error">{error}</div>}
      {done && <div className="co-success" role="status">{done} Taking you to the dashboard…</div>}

      {catalog && !invalid && !done && (
        <div className="co-actions">
          <button
            type="button"
            className="billing-btn co-pay"
            disabled={busy}
            onClick={isPayg ? activatePayg : () => void payForPlan()}
          >
            {busy ? 'Working…' : isPayg ? 'Activate Pay as you go' : plan && plan.priceMonthly > 0 && !autopay ? 'Pay for 1 month' : 'Pay now'}
          </button>
        </div>
      )}

      {addressModalOpen && (
        <BillingAddressModal
          onClose={() => setAddressModalOpen(false)}
          onSaved={() => {
            setAddressModalOpen(false);
            void payForPlan(true);
          }}
        />
      )}
    </div>
  );
};

export default CheckoutPage;
